import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import SectionHeading from "@/components/common/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FORMS } from "@/constants/testIds";
import { postForm } from "@/lib/api";

const initial = { name: "", email: "", phone: "", company: "", country: "", partner_type: "country_rep", experience_years: "", industries: "", linkedin: "", motivation: "" };

const benefits = [
    { tKey: "partnerPage.benefits.realProduct.title", dKey: "partnerPage.benefits.realProduct.description" },
    { tKey: "partnerPage.benefits.categoryDepth.title", dKey: "partnerPage.benefits.categoryDepth.description" },
    { tKey: "partnerPage.benefits.oemPrograms.title", dKey: "partnerPage.benefits.oemPrograms.description" },
    { tKey: "partnerPage.benefits.commission.title", dKey: "partnerPage.benefits.commission.description" },
];

export default function GlobalPartner() {
    const { t } = useTranslation();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postForm("/partner", form);
            toast.success(t("toast.partnerReceived"));
            setForm(initial);
        } catch { toast.error(t("toast.genericError")); }
        finally { setLoading(false); }
    };
    return (
        <>
            <SEO
                title="Become Our Global Partner — Country Reps, Buying Offices, Distributors | Exportfy.world"
                description="Become Exportfy.world's country representative, buying office, distributor, or commission agent. Apply to represent a 5-year-old operating Indian merchant exporter in your market."
                path="/global-partner"
            />
            <PageHero
                eyebrow={t("partnerPage.hero.eyebrow")}
                title={t("partnerPage.hero.title")}
                subtitle={t("partnerPage.hero.subtitle")}
                image="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1600"
            />
            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
                    <ScrollReveal>
                        <SectionHeading eyebrow={t("partnerPage.benefits.eyebrow")} title={t("partnerPage.benefits.title")} />
                        <ul className="mt-8 space-y-5">
                            {benefits.map((b) => (
                                <li key={b.tKey} className="flex gap-4">
                                    <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 font-bold">✓</span>
                                    <div>
                                        <div className="font-bold text-[#0A2540]">{t(b.tKey)}</div>
                                        <p className="text-slate-500 text-sm mt-1">{t(b.dKey)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </ScrollReveal>
                    <ScrollReveal delay={80}>
                        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-900/5 p-7">
                            <h3 className="text-2xl font-extrabold text-[#0A2540]">{t("partnerPage.form.title")}</h3>
                            <p className="text-sm text-slate-500 mt-1">{t("partnerPage.form.subtitle")}</p>
                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Input required placeholder={t("partnerPage.form.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={FORMS.partner.name} />
                                <Input required type="email" placeholder={t("partnerPage.form.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={FORMS.partner.email} />
                                <Input placeholder={t("partnerPage.form.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={FORMS.partner.phone} />
                                <Input placeholder={t("partnerPage.form.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={FORMS.partner.company} />
                                <Input required placeholder={t("partnerPage.form.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid={FORMS.partner.country} />
                                <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                                    <SelectTrigger data-testid={FORMS.partner.type}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="country_rep">{t("partnerPage.form.types.countryRepresentative")}</SelectItem>
                                        <SelectItem value="buying_office">{t("partnerPage.form.types.buyingOffice")}</SelectItem>
                                        <SelectItem value="distributor">{t("partnerPage.form.types.distributor")}</SelectItem>
                                        <SelectItem value="commission_agent">{t("partnerPage.form.types.commissionAgent")}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input placeholder={t("partnerPage.form.experience")} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} data-testid={FORMS.partner.experience} />
                                <Input placeholder={t("partnerPage.form.linkedin")} value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} data-testid={FORMS.partner.linkedin} />
                            </div>
                            <Input className="mt-3" placeholder={t("partnerPage.form.industries")} value={form.industries} onChange={(e) => setForm({ ...form, industries: e.target.value })} data-testid={FORMS.partner.industries} />
                            <Textarea rows={3} className="mt-3" placeholder={t("partnerPage.form.motivation")} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} data-testid={FORMS.partner.motivation} />
                            <Button type="submit" disabled={loading} data-testid={FORMS.partner.submit} className="mt-5 w-full rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold py-6">
                                {loading ? t("partnerPage.form.submitting") : t("partnerPage.form.submit")}
                            </Button>
                        </form>
                    </ScrollReveal>
                </div>
            </section>
        </>
    );
}
